<kendo-gridlayout class="child-flows-report"
  [cols]="['','']"
  [gap]="{ rows: 8, cols: 8 }">

  <kendo-gridlayout-item [col]="1" [colSpan]="2" [rowSpan]="2">
    <div class="example-box" style="display:flex; justify-content: space-between; flex-wrap: wrap;">
      <div></div>
      <div>
        <span class="text-sm text-slate-500">Flows (Child Flows) is a <span class="pro-box"></span> subscription feature</span>
      </div>
    </div>
  </kendo-gridlayout-item>

  <kendo-gridlayout-item [col]="1" [colSpan]="2" [rowSpan]="2">
    <div style="padding:8px;"><b>Flows (Child Flows) Report</b></div>

    <ul>
      <li>
        This report lists every <b>manually-triggered (Button) flow</b> in the current environment — i.e. every flow that <i>could</i> be invoked as a child flow — and notes whether each runs as the <b>caller's user</b> (Invoker connection) or as the <b>flow owner / service account</b> (embedded connection).
      </li>
      <li>
        Pipeline: (1) one listing call (owner + shared + solution); (2) filter to <code>trigger = Request</code> &amp; <code>kind = Button</code> using the trigger summary already on the Flow object — no per-flow API call needed; (3) one <code>getFlow</code> per surviving candidate to read <code>connectionReferences</code> ({{ candidates_done() }}/{{ candidates_total() }}); (4) one <code>getFlowRuns</code> per row for the last-run column ({{ runs_done() }}/{{ runs_total() }}).
      </li>
      <li>
        <b>Invoker</b> — at least one connection has <code>source: "Invoker"</code> (credentials passed in by the caller). <b>Service Account</b> — every connection is embedded (uses the flow owner's connection). <b>Mixed</b> rows are worth a closer look — uncommon and may be unintentional. <b>Unknown</b> — connectionReferences were unreadable (e.g. a solution flow the user can't see and admin scope also failed); check the browser console for the failing flowName.
      </li>
    </ul>

    @if (status()) {
      <div style="padding: 4px 8px; color:#555; font-size: 12px;">
        <span>{{ status() }}</span>
      </div>
    }
  </kendo-gridlayout-item>

  <kendo-gridlayout-item [col]="1" [colSpan]="2" [rowSpan]="2">

    <!--
    Toolbar — icon-only buttons (28px) sharing the `.tb-btn` style used
    elsewhere in the app. Selection-scoped buttons disable when nothing
    relevant is selected. The search box filters across all visible
    columns (case-insensitive, joined into one haystack per row).
    -->
    <div class="flex flex-wrap items-center gap-1 px-2 py-1.5 cf-toolbar">
      <button type="button" class="tb-btn"
        (click)="load()" [disabled]="loading()"
        [title]="loading() ? 'Loading…' : 'Load Report'">
        <i class="ms-Icon" [ngClass]="loading() ? 'ms-Icon--Sync cf-spin' : 'ms-Icon--CRMReport'" aria-hidden="true"></i>
      </button>
      <button type="button" class="tb-btn"
        (click)="downloadCsv()"
        [disabled]="child_rows()?.length===0 || loading()"
        title="Export Excel">
        <i class="ms-Icon ms-Icon--ExcelDocument" aria-hidden="true"></i>
      </button>

      <div class="mx-1 h-5 w-px bg-slate-200" aria-hidden="true"></div>

      <button type="button" class="tb-btn"
        [disabled]="!canReplaceConnections()"
        (click)="openReplaceConnections()"
        title="Replace Invoker connections on selected flows">
        <i class="ms-Icon ms-Icon--PlugConnected" aria-hidden="true"></i>
      </button>

      @if (selection.length) {
        <span class="text-xs text-slate-500 ml-1">
          {{ selection.length }} selected@if (selectedInvokerCount() && selectedInvokerCount() !== selection.length) {
          <span>
          · {{ selectedInvokerCount() }} with Invoker</span>
        }
      </span>
    }

    <input type="search" placeholder="Search"
      class="cf-search inline-flex items-center rounded-lg shadow border-0 px-3 h-8 text-xs text-slate-600 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-200 ml-auto"
      [value]="search()"
      (input)="search.set($any($event.target).value)" />
  </div>

  <kendo-grid class="child-flows-report__grid"
    #grid
    [data]="view()"
    [style.height]="'100%'"
    [sortable]="true"
    filterable="menu"
    [selectable]="{ enabled: true, checkboxOnly: false, mode: 'multiple' }"
    [kendoGridSelectBy]="'flowName'"
    [(selectedKeys)]="selection">
    <kendo-grid-checkbox-column [showSelectAll]="true" [width]="28"
      [style]="{'max-width':'28px','min-width':'28px','padding':'4px 0 4px 8px'}"
    [headerStyle]="{'max-width':'28px','min-width':'28px','padding':'4px 0 4px 8px'}"></kendo-grid-checkbox-column>
    <kendo-grid-column field="environment" title="Environment" [width]="200" [filterable]="true" [sortable]="true">
    </kendo-grid-column>
    <kendo-grid-column field="displayName" title="Child Flow" [width]="260" [filterable]="true" [sortable]="true">
      <ng-template kendoGridCellTemplate let-dataItem>
        @if (dataItem.portalUrl) {
          <a [href]="dataItem.portalUrl" target="_blank"
            [title]="dataItem.resolved ? dataItem.flowName : 'Unresolved — opens in portal by workflow id'">
            {{ dataItem.displayName }}
            @if (!dataItem.resolved) {
              <i class="ms-Icon ms-Icon--Warning" style="color:#b15a0b; margin-left:4px;" aria-hidden="true"></i>
            }
          </a>
        }
        @if (!dataItem.portalUrl) {
          <span>{{ dataItem.displayName }}</span>
        }
      </ng-template>
    </kendo-grid-column>
    <kendo-grid-column field="flowState" title="State" [width]="110" [sortable]="true">
      <ng-template kendoGridFilterMenuTemplate let-column="column" let-filter="filter" let-filterService="filterService">
        <multicheck-filter
          [isPrimitive]="true"
          [field]="column.field"
          [filterService]="filterService"
          [currentFilter]="filter"
        [data]="stateValues()"></multicheck-filter>
      </ng-template>
    </kendo-grid-column>
    <kendo-grid-column field="runMode" title="Run As" [width]="180" [sortable]="true">
      <ng-template kendoGridFilterMenuTemplate let-column="column" let-filter="filter" let-filterService="filterService">
        <multicheck-filter
          [isPrimitive]="true"
          [field]="column.field"
          [filterService]="filterService"
          [currentFilter]="filter"
        [data]="runModeValues"></multicheck-filter>
      </ng-template>
      <ng-template kendoGridCellTemplate let-dataItem>
        <span [title]="dataItem.runModeDetail">
          @switch (dataItem.runMode) {
            @case ('Invoker') {
              <span style="color:#0b6cb1; font-weight:600;">User-credentials (Invoker)</span>
            }
            @case ('Service Account') {
              <span style="color:#1f7a3e; font-weight:600;">Service Account</span>
            }
            @case ('Mixed') {
              <span style="color:#b15a0b; font-weight:600;">⚠ Mixed</span>
            }
            @default {
              <span style="color:#888;">Unknown</span>
            }
          }
        </span>
      </ng-template>
    </kendo-grid-column>
    <kendo-grid-column field="runModeDetail" title="Conn Detail" [width]="160" [filterable]="false" [sortable]="true">
    </kendo-grid-column>
    <kendo-grid-column field="connectorsText" title="Connections" [sortable]="false">
      <ng-template kendoGridFilterMenuTemplate let-column="column" let-filter="filter" let-filterService="filterService">
        <multicheck-filter
          [isPrimitive]="true"
          [field]="column.field"
          [filterService]="filterService"
          [currentFilter]="filter"
        [data]="connectorValues()"></multicheck-filter>
      </ng-template>
      <ng-template kendoGridCellTemplate let-dataItem>
        @for (ref of dataItem.connectionRefs; track ref; let last = $last) {
          <span
            [title]="ref.name + ' → ' + ref.api + ' (' + ref.mode + ')'"
            [style.color]="ref.mode === 'Invoker' ? '#0b6cb1' : (ref.mode === 'Embedded' ? '#1f7a3e' : '#555')">
            {{ ref.api || ref.name }} <small>({{ ref.mode }})</small>@if (!last) {
            <span>, </span>
          }
        </span>
      }
    </ng-template>
  </kendo-grid-column>
  <kendo-grid-column field="lastRun" title="Last Run" [width]="180" [filterable]="true" [sortable]="true">
    <ng-template kendoGridCellTemplate let-dataItem>
      {{ dataItem.lastRun ? (dataItem.lastRun | date:'yyyy-MM-dd HH:mm:ss') : 'Never' }}
    </ng-template>
  </kendo-grid-column>

  <kendo-grid-excel fileName="flow-child-flows.xlsx"></kendo-grid-excel>
</kendo-grid>
</kendo-gridlayout-item>

</kendo-gridlayout>
